import axios from "axios";
import { showGlobalError } from "../App";

// Read API base URL from environment variables
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const popupMessage =
  "Network error - please check your connection or try again later.";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true,
  transformResponse: [
    (data, headers) => {
      // Handle HTML responses
      if (headers["content-type"]?.includes("text/html")) {
        console.error("Received HTML response instead of JSON");
        showGlobalError(popupMessage);
        throw new Error("Server connection error");
      }

      try {
        return JSON.parse(data);
      } catch (e) {
        console.error("Failed to parse response as JSON:", e);
        return data;
      }
    },
  ],
});

// Add request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration and ngrok errors
api.interceptors.response.use(
  (response) => {
    // Check if response is HTML (ngrok error page)
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("text/html")) {
      console.error("Received HTML response instead of JSON");
      showGlobalError(popupMessage);
      return Promise.reject(new Error("Server connection error"));
    }
    return response;
  },
  (error) => {
    // Log detailed error information
    console.error("API Error:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      headers: error.response?.headers,
      data: error.response?.data,
      config: error.config,
    });

    // Handle CORS errors
    if (!error.response) {
      console.error("Network error - possible CORS issue");
      showGlobalError(popupMessage);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 || isTokenExpired()) {
      // Token expired or invalid
      logout();
    }
    return Promise.reject(error);
  }
);

export const checkHealth = async () => {
  try {
    const response = await api.get("/health");
    return response.data;
  } catch (error) {
    console.error("Health check failed:", error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    // Ensure all required fields are present

    const signupData = {
      email: userData.user.email,
      first_name: "",
      last_name: "",
      uid: userData.user.uid,
      phone: "" || null,
      email_verified: userData.user.emailVerified,
    };

    await api.post("/users", signupData);
  } catch (error) {
    throw new Error(error.response?.data?.detail || "User Registration Failed");
  }
};

export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No token found");
    }

    const response = await api.get(`/users/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/auth";
    }
    throw new Error(
      error.response?.data?.detail || "Failed to fetch user data"
    );
  }
};

export const getUserCredits = async () => {
  try {
    const response = await api.get("/users/me/credits");
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/auth";
    }
    throw new Error(
      error.response?.data?.detail || "Failed to fetch user credits"
    );
  }
};

export const getUserNotifications = async () => {
  try {
    const response = await api.get("/users/me/notifications");
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/auth";
    }
    throw new Error(
      error.response?.data?.detail || "Failed to fetch notifications"
    );
  }
};

// User settings functions
export const getUserSettings = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No token found");
    }

    const response = await api.get("/auth/me", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/auth";
    }
    throw new Error(
      error.response?.data?.detail || "Failed to fetch user settings"
    );
  }
};

export const updateUser = async (userData) => {
  try {
    const response = await api.put("/users/me", userData);
    return response.data;
  } catch (error) {
    console.error("Update failed:", error.response?.data);
    throw new Error(
      error.response?.data?.detail || "Failed to update user settings"
    );
  }
};

// Job functions
export const createJob = async (userInput) => {
  try {
    const response = await api.post("/jobs", userInput);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Failed to create job");
  }
};

export const getJobStatus = async (jobId) => {
  try {
    const response = await api.get(`/jobs/${jobId}/status`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Failed to get job status");
  }
};

export const getJobResults = async (jobId) => {
  try {
    const response = await api.get(`/jobs/${jobId}/results`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || "Failed to get job results"
    );
  }
};

export const getJobHistory = async () => {
  try {
    const response = await api.get("/history");
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || "Failed to get job history"
    );
  }
};

export const cancelJob = async (jobId) => {
  try {
    const response = await api.delete(`/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "Failed to cancel job");
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem("token");
};

// Get current user token
export const getToken = () => {
  return localStorage.getItem("token");
};

export const submitFeedback = async (feedbackData) => {
  try {
    const response = await api.post("/feedback", feedbackData);
    return response.data;
  } catch (error) {
    console.error("Error submitting feedback:", error);
    throw new Error(
      error.response?.data?.detail || "Failed to submit feedback"
    );
  }
};

// Check if token is expired
export const isTokenExpired = () => {
  const token = localStorage.getItem("token");
  if (!token) return true;

  try {
    // Firebase tokens are JWT, we can decode them to check expiration
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    const { exp } = JSON.parse(jsonPayload);
    return exp * 1000 < Date.now();
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
};

// Enhanced logout function
export const logout = () => {
  localStorage.removeItem("token");
  window.location.href = "/";
};

export const markNotificationRead = async (notificationId) => {
  try {
    const response = await api.put(
      `/users/me/notifications/${notificationId}/read`
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/auth";
    }
    throw new Error(
      error.response?.data?.detail || "Failed to mark notification as read"
    );
  }
};

export const checkSocialMediaUsername = async (platform, username) => {
  try {
    const response = await api.get("/social-check", {
      params: { platform, username },
    });
    return response.data.exists;
  } catch (error) {
    console.error(
      `Failed to check username ${username} on ${platform}:`,
      error
    );
    return false; // Assume not available on error
  }
};
